// @ts-check

/**
 * CUSTOM: Luksus-billedgalleri på produktsiden. Markup: snippets/custom-product-gallery.liquid
 * Swipe er ren CSS scroll-snap (følger fingeren 1:1 som hos Josephine Nord). JS styrer kun:
 * - aktivt thumbnail (streg under) mens der swipes, og tryk på thumbnail → hop til billedet
 * - ←/→ når slideren har fokus
 * - variantskift (Motions 'variantChange' + 'custom:variant-change' fra custom-variant-sync.js): mobil → slideren viser variantens billede;
 *   desktop → variantens billede rykker op som billede 1 med blød overgang, variant uden eget billede → normal rækkefølge
 * - sticky: Motions sticky billedkolonne slås fra, hvis galleriet er højere end skærmen
 * Zoom klares af Motions egen PhotoSwipe (theme.js) via .js-photoswipe__zoom.
 * Ren logik i CustomProductGallery.activeIndex(), .resolveActive(), .orderFor() og .shouldStick() – kan testes isoleret.
 */
(function () {
  if (customElements.get('custom-product-gallery')) return;

  /** Afstand fra toppen, når Motions billedkolonne er sticky (theme.css: .product-single__sticky { top: 20px }) */
  const STICKY_TOP = 20;

  /**
   * Ren logik: hvilket billede er aktivt i slideren?
   * @param {number} scrollLeft
   * @param {number} slideWidth
   * @param {number} count
   * @returns {number} 0-baseret index
   */
  function activeIndex(scrollLeft, slideWidth, count) {
    if (slideWidth <= 0 || count <= 0) return 0;
    return Math.min(count - 1, Math.max(0, Math.round(scrollLeft / slideWidth)));
  }

  /**
   * Ren logik: hvilket thumbnail skal have stregen under en scroll?
   * Ved tryk på et thumbnail kører slideren forbi billederne imellem. Indtil den er fremme ved målet (pending),
   * ignoreres de mellemliggende billeder, så stregen går direkte til det valgte billede (rettet 12/10 2026).
   * @param {number} scrollLeft
   * @param {number} slideWidth
   * @param {number} count
   * @param {number | null} pending mål for et igangværende hop, ellers null
   * @returns {{ active: number | null, pending: number | null }} active = null → lad stregen stå
   */
  function resolveActive(scrollLeft, slideWidth, count, pending) {
    const index = activeIndex(scrollLeft, slideWidth, count);
    if (pending === null) return { active: index, pending: null };
    const arrived = Math.abs(scrollLeft - pending * slideWidth) < 2;
    return arrived ? { active: pending, pending: null } : { active: null, pending };
  }

  /**
   * Ren logik: billedrækkefølge på desktop for en variant.
   * @param {string[]} initial media-id'er i den oprindelige rækkefølge
   * @param {string | null} mediaId variantens billede (null = varianten har intet eget billede)
   * @returns {string[]}
   */
  function orderFor(initial, mediaId) {
    if (!mediaId || initial.indexOf(mediaId) === -1) return initial.slice();
    return [mediaId].concat(initial.filter((id) => id !== mediaId));
  }

  /**
   * Ren logik: må billedkolonnen være sticky? Kun hvis hele galleriet kan ses på én skærm.
   * @param {number} galleryHeight
   * @param {number} viewportHeight
   * @param {number} top
   * @returns {boolean}
   */
  function shouldStick(galleryHeight, viewportHeight, top) {
    return galleryHeight + top * 2 <= viewportHeight;
  }

  /**
   * Ren logik: sticky-top for billedkolonnen (16/10 2026, som Josephine Nord – billederne følger med ned).
   * Kan hele galleriet ses: klæb i toppen (top). Er galleriet højere end skærmen: klæb med BUNDEN
   * (negativ top), så galleriet scroller med, indtil det sidste billede står nederst i skærmen, og bliver dér.
   * @param {number} galleryHeight
   * @param {number} viewportHeight
   * @param {number} top
   * @returns {number}
   */
  function stickyTop(galleryHeight, viewportHeight, top) {
    if (shouldStick(galleryHeight, viewportHeight, top)) return top;
    return Math.round(viewportHeight - galleryHeight - top);
  }

  class CustomProductGallery extends HTMLElement {
    connectedCallback() {
      /** @type {HTMLElement | null} */
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      /** @type {HTMLElement[]} */
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      /** @type {HTMLButtonElement[]} */
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      /** @type {HTMLElement | null} */
      this.thumbBar = this.querySelector('[data-gallery-thumbs]');
      this.current = 0;
      /** @type {string[]} oprindelig rækkefølge (media-id) – variant uden eget billede vender tilbage hertil */
      this.initialOrder = this.items.map((item) => item.dataset.mediaId || '');
      /** @type {number | null} mål for et igangværende hop (tryk på thumbnail / variantskift) */
      this.pending = null;
      /** @type {number | undefined} */
      this.pendingTimer = undefined;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)));
      });

      /** @type {number | undefined} */
      let frame;
      this.track.addEventListener(
        'scroll',
        () => {
          if (frame) cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.sync());
        },
        { passive: true }
      );

      // Rører kunden selv slideren, overtager fingeren – stregen følger igen billedet direkte
      const release = () => {
        this.pending = null;
      };
      this.track.addEventListener('touchstart', release, { passive: true });
      this.track.addEventListener('pointerdown', release, { passive: true });
      this.track.addEventListener('wheel', release, { passive: true });

      this.track.addEventListener('keydown', (e) => {
        if (!this.isSlider()) return;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          this.goTo(this.current + (e.key === 'ArrowRight' ? 1 : -1));
        }
      });

      // Variantskift: Motions dropdowns sender 'variantChange' på produkt-sektionen; de custom vælgere (kæde, antal navne)
      // sender 'custom:variant-change' på document (assets/custom-variant-sync.js). Også varianter uden eget billede.
      /** @param {Event} e */
      const onVariant = (e) => {
        const detail = /** @type {CustomEvent} */ (e).detail;
        const variant = detail && detail.variant;
        if (!variant) return;
        const mediaId = variant.featured_media && variant.featured_media.id;
        this.showVariantMedia(mediaId ? String(mediaId) : null);
      };
      const section = this.closest('[data-section-type="product"]');
      if (section) section.addEventListener('variantChange', onVariant);
      document.addEventListener('custom:variant-change', onVariant);

      if ('ResizeObserver' in window) {
        new ResizeObserver(() => this.updateSticky()).observe(this);
      }
      window.addEventListener('resize', () => this.updateSticky(), { passive: true });
      this.updateSticky();
    }

    /** Slider (mobil/tablet) eller gitter (desktop)? Afgøres af CSS'en, så JS og CSS altid er enige. */
    isSlider() {
      return !!this.track && getComputedStyle(this.track).display === 'flex';
    }

    /** @param {string | null} mediaId */
    showVariantMedia(mediaId) {
      if (!this.track) return;
      if (this.isSlider()) {
        if (!mediaId) return;
        const index = this.items.findIndex((item) => item.dataset.mediaId === mediaId);
        if (index > -1) this.goTo(index);
        return;
      }

      const order = orderFor(this.initialOrder, mediaId);
      const now = this.items.map((item) => item.dataset.mediaId || '');
      if (order.join() === now.join()) return;

      /** @param {HTMLElement[]} list */
      const sortBy = (list) =>
        order.map((id) => list.find((el) => el.dataset.mediaId === id)).filter(/** @returns {el is HTMLElement} */ (el) => !!el);
      this.items = sortBy(this.items);
      // Billedet der rykker op som nr. 1 var måske lazy og ikke hentet endnu – hent det med det samme (ingen tomt felt)
      const firstImg = this.items[0] && this.items[0].querySelector('img');
      if (firstImg) firstImg.loading = 'eager';
      this.items.forEach((item) => this.track && this.track.appendChild(item));
      this.thumbs = /** @type {HTMLButtonElement[]} */ (sortBy(this.thumbs));
      this.thumbs.forEach((thumb, i) => {
        thumb.dataset.index = String(i);
        if (this.thumbBar) this.thumbBar.appendChild(thumb);
      });
      // Motions zoom bruger 1-baseret data-index i DOM-rækkefølge
      this.track.querySelectorAll('.js-photoswipe__zoom').forEach((btn, i) => {
        /** @type {HTMLElement} */ (btn).dataset.index = String(i + 1);
      });
      this.current = -1;
      this.setActive(0);

      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && this.track.animate) {
        this.track.animate([{ opacity: 0.2 }, { opacity: 1 }], { duration: 350, easing: 'ease-out' });
      }
    }

    /** @param {number} index */
    goTo(index) {
      if (!this.track || !this.isSlider()) return;
      const target = Math.min(this.items.length - 1, Math.max(0, index));
      const targetImg = this.items[target] && this.items[target].querySelector('img');
      if (targetImg) targetImg.loading = 'eager';
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.pending = target;
      // Sikkerhedsnet: slip låsen, hvis scrollen aldrig når helt frem (fx afbrudt)
      clearTimeout(this.pendingTimer);
      this.pendingTimer = window.setTimeout(() => {
        this.pending = null;
        this.sync();
      }, 1000);
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
      this.setActive(target);
    }

    sync() {
      if (!this.track) return;
      const result = resolveActive(this.track.scrollLeft, this.track.clientWidth, this.items.length, this.pending);
      this.pending = result.pending;
      if (result.active !== null) this.setActive(result.active);
    }

    /** @param {number} index */
    setActive(index) {
      if (index === this.current && this.thumbs[index]?.getAttribute('aria-current') === 'true') return;
      this.current = index;
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      // Hold det aktive thumbnail synligt – flytter kun thumbnail-rækken, aldrig siden
      const thumb = this.thumbs[index];
      if (thumb && this.thumbBar && this.thumbBar.scrollWidth > this.thumbBar.clientWidth) {
        const left = thumb.offsetLeft - this.thumbBar.offsetLeft;
        const right = left + thumb.offsetWidth;
        const bar = this.thumbBar;
        if (left < bar.scrollLeft) bar.scrollTo({ left, behavior: 'smooth' });
        else if (right > bar.scrollLeft + bar.clientWidth) bar.scrollTo({ left: right - bar.clientWidth, behavior: 'smooth' });
      }
    }

    updateSticky() {
      // Sticky header (custom-sticky-header.js): kolonnen står under headeren, så den har mindre plads
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--custom-header-height')) || 0;
      const top = stickyTop(this.offsetHeight, window.innerHeight - headerHeight, STICKY_TOP);
      // Sættes på Motions sticky-kolonne (.product-single__sticky) – se custom-product-gallery.css
      const column = this.parentElement;
      if (column) column.style.setProperty('--custom-pg-sticky-top', top + 'px');
    }
  }

  /** @type {any} */ (CustomProductGallery).activeIndex = activeIndex;
  /** @type {any} */ (CustomProductGallery).resolveActive = resolveActive;
  /** @type {any} */ (CustomProductGallery).orderFor = orderFor;
  /** @type {any} */ (CustomProductGallery).shouldStick = shouldStick;
  /** @type {any} */ (CustomProductGallery).stickyTop = stickyTop;
  customElements.define('custom-product-gallery', CustomProductGallery);
})();
